import { t } from '@lingui/macro'
import {
    Address,
    AirportCode,
    EmissionFactorDenominatorUnit,
    EmissionFactorWithGasEmissions,
    GeographicCoordinates,
    Load,
    Locode,
    LogisticsSiteMethod,
    ShippingMethod,
    ShippingRoute,
    ShippingSourceDestination,
} from '@lune-climate/lune'

import { formatDate } from '../../../utils'
import { EmissionEstimationIcons } from '../../emissionEstimationIcons'
import { ResultTileProps } from '../ResultLabeledSectionAndTiles'

import {
    convertDDCoordinatesToDMS,
    formatAddress,
    FORMATTED_CARRIER_NAMES,
    getEmptyRunsFactorLabel,
    getLoadFactorLabel,
    getLoadLabel,
    getRoadEmissionStandard,
    getRoadGrandientAndSituationLabel,
    getRoadVehicleName,
    getShippingMethodLabel,
    getTradeLaneLabel,
    isLogisticsSiteMethod,
    typeIcon,
} from './logisticsUtils'

const buildDateTiles = (departureOn?: string, arrivalOn?: string): ResultTileProps[] => [
    ...(departureOn
        ? [
              {
                  label: <>Departure {formatDate(departureOn)}</>,
                  icon: EmissionEstimationIcons.calendar,
              },
          ]
        : []),
    ...(arrivalOn
        ? [{ label: <>Arrival {formatDate(arrivalOn)}</>, icon: EmissionEstimationIcons.calendar }]
        : []),
]

export const buildLogisticsLabelExtension = (
    key: string,
    value: string | boolean | object,
): string | null => {
    switch (key) {
        case 'fuel': {
            return value as string
        }
        case 'load': {
            return getLoadLabel(value as string)
        }
        case 'tradeLane': {
            return getTradeLaneLabel(value as string)
        }
        case 'refrigerated': {
            if (value) {
                return 'refrigerated'
            }
            break
        }
        default:
            return null
    }
    return null
}

const formatLabelExtension = (extensions: string[]): string => {
    return extensions.length ? `, ${extensions.join(', ')}` : ''
}

// eslint-disable-next-line complexity
export const buildLogisticsTypeTiles = (
    value: ShippingMethod | LogisticsSiteMethod,
    emissionFactor: EmissionFactorWithGasEmissions,
): ResultTileProps[] => {
    const labelExtension = Object.entries(value)
        .map(([key, value]) => buildLogisticsLabelExtension(key, value))
        .filter((e): e is Exclude<typeof e, null> => e !== null)

    if (value instanceof Object && 'flightNumber' in value) {
        return [
            {
                label: <>Flight number {value.flightNumber}</>,
                icon: EmissionEstimationIcons.flights,
            },
            ...buildDateTiles(value.departureOn),
        ]
    }
    if (value instanceof Object && 'aircraftType' in value) {
        return [
            {
                label: <>{value.aircraftType}</>,
                icon: EmissionEstimationIcons.plane,
            },
        ]
    }
    if (value === 'rail') {
        return [
            {
                label: <>Rail</>,
                icon: EmissionEstimationIcons.train,
            },
        ]
    }
    if (value instanceof Object && 'vesselImoNumber' in value) {
        return [
            {
                label: <>IMO {value.vesselImoNumber}</>,
                icon: EmissionEstimationIcons.number,
            },
            ...buildDateTiles(value.vesselTracking?.departureOn, value.vesselTracking?.arrivalOn),
        ]
    } else if (value instanceof Object && 'vesselName' in value) {
        return [
            {
                label: <>{value.vesselName}</>,
                icon: EmissionEstimationIcons.ship,
            },
            ...buildDateTiles(value.vesselTracking?.departureOn, value.vesselTracking?.arrivalOn),
        ]
    } else if (value instanceof Object && 'vesselInference' in value) {
        return [
            {
                label: <>{FORMATTED_CARRIER_NAMES[value.vesselInference.carrierName]}</>,
                icon: EmissionEstimationIcons.ship,
            },
            ...buildDateTiles(value.vesselInference.departureOn, value.vesselInference.arrivalOn),
        ]
    } else if (value instanceof Object && 'vesselType' in value) {
        return [
            {
                label: (
                    <>
                        {getShippingMethodLabel(value.vesselType) +
                            formatLabelExtension(labelExtension)}
                    </>
                ),
                icon: typeIcon(value.vesselType) ?? null,
            },
        ]
    } else if (value instanceof Object && 'vehicleType' in value && 'fuel' in value) {
        const loadFactors = [
            ...(value.loadFactor ? [getLoadFactorLabel(value.loadFactor)] : []),
            ...(value.emptyRunsFactor ? [getEmptyRunsFactorLabel(value.emptyRunsFactor)] : []),
        ]
        return [
            {
                label: (
                    <>
                        {getRoadVehicleName(value.vehicleType)}
                        {value.emissionStandard
                            ? `, ${getRoadEmissionStandard(value.emissionStandard, { shortForm: true })}`
                            : null}
                    </>
                ),
                icon: EmissionEstimationIcons.truck,
            },
            {
                label: <>{emissionFactor.name}</>,
                icon: EmissionEstimationIcons.fuel,
            },
            ...(loadFactors.length > 0
                ? [
                      {
                          label: <>{loadFactors.join(', ')}</>,
                          icon: EmissionEstimationIcons.package,
                      },
                  ]
                : []),
            ...(value.gradient || value.situation
                ? [
                      {
                          label: (
                              <>
                                  {getRoadGrandientAndSituationLabel(
                                      value.gradient,
                                      value.situation,
                                  )}
                              </>
                          ),
                          icon: EmissionEstimationIcons.road, // TODO: fix incorrect icon, ask Ale
                      },
                  ]
                : []),
        ]
    } else if (isLogisticsSiteMethod(value)) {
        return [
            {
                label: <>{getShippingMethodLabel(value)}</>,
                icon: EmissionEstimationIcons.warehouse,
            },
        ]
    } else {
        return [
            {
                label: <>{getShippingMethodLabel(value) + formatLabelExtension(labelExtension)}</>,
                icon: typeIcon(value) ?? null,
            },
        ]
    }
}

export const getSourceOrDestination = (
    location: Locode | GeographicCoordinates | AirportCode | Address | string,
    label: string,
): ResultTileProps => {
    // Locode
    if (typeof location === 'string') {
        const icon = EmissionEstimationIcons.location
        return {
            icon,
            label: `${label} ${location}`,
        }
    }
    if ('locode' in location) {
        const icon = EmissionEstimationIcons.location
        return {
            icon,
            label: `${label} ${location.locode}`,
        }
    }

    // GeographicCoordinates
    if ('lat' in location) {
        const icon = EmissionEstimationIcons.location
        return {
            icon,
            label: `${label} ${convertDDCoordinatesToDMS(
                Number(location.lat.toFixed(2)),
            )}, ${convertDDCoordinatesToDMS(Number(location.lon.toFixed(2)), true)}`,
        }
    }

    // AirportCode
    if ('code' in location) {
        const icon = EmissionEstimationIcons.plane
        return {
            icon,
            label: `${label} ${location.code}`,
        }
    }

    // Address
    return {
        icon: EmissionEstimationIcons.address,
        label: `${label} ${formatAddress(location)}`,
    }
}

export const getShippingSourceDestinationTile = (
    route: ShippingSourceDestination,
): ResultTileProps[] => {
    const source: ResultTileProps = getSourceOrDestination(route.source, t`From`)
    const destination: ResultTileProps = getSourceOrDestination(route.destination, t`To`)
    return [source, destination]
}

export const buildRouteTiles = (route: ShippingRoute) => {
    if ('amount' in route) {
        // shipping distance
        return [
            {
                icon: EmissionEstimationIcons.route,
                label: `${route.amount} ${route.unit}`,
            },
        ]
    } else {
        // shipping source and destination
        return getShippingSourceDestinationTile(route)
    }
}

export const loadLabel = (
    shipment: Load,
    emissionFactorDenominatorUnit: EmissionFactorDenominatorUnit,
): string => {
    // * if both mass and containers are present => check denominatorUnit to determine whether containers should be shown
    if ('mass' in shipment && 'containers' in shipment) {
        return ['TEU', 'TEU*km'].includes(emissionFactorDenominatorUnit)
            ? `${shipment.containers} TEU`
            : `${shipment.mass!.amount} ${shipment.mass!.unit}`
    }

    return 'containers' in shipment
        ? `${shipment.containers} TEU`
        : `${shipment.mass.amount} ${shipment.mass.unit}`
}

export const buildLoadTiles = (
    shipment: Load,
    emissionFactorDenominatorUnit: EmissionFactorDenominatorUnit,
) => {
    return [
        {
            label: <>{loadLabel(shipment, emissionFactorDenominatorUnit)}</>,
            icon: EmissionEstimationIcons.load,
        },
    ]
}
